import { supabase } from '@/integrations/supabase/client';

export interface PracticeProgress {
  id: string;
  userId: string;
  catalogId: string;
  currentQuestionNumber: number;
  completedQuestions: number[];
  selectedState?: string | null;
  totalQuestions: number;
  lastPracticedAt: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Get or create practice progress for a user and catalog
 */
export async function getOrCreatePracticeProgress(
  userId: string,
  catalogId: string,
  selectedState?: string | null,
  totalQuestions: number = 0
): Promise<PracticeProgress | null> {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // First try to get existing progress
    let query = supabase
      .from('user_practice_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('catalog_id', catalogId);

    if (selectedState) {
      query = query.eq('selected_state', selectedState);
    } else {
      query = query.is('selected_state', null);
    }

    const { data: existingProgress, error: selectError } = await query.single();

    if (selectError && selectError.code !== 'PGRST116') {
      throw selectError;
    }

    if (existingProgress) {
      // Parse completed questions from string format
      const completedQuestions = existingProgress.completed_questions
        ? existingProgress.completed_questions
            .split(',')
            .map(Number)
            .filter(Boolean)
        : [];

      return {
        id: existingProgress.id,
        userId: existingProgress.user_id,
        catalogId: existingProgress.catalog_id,
        currentQuestionNumber: existingProgress.current_question_number,
        completedQuestions,
        selectedState: existingProgress.selected_state,
        totalQuestions: existingProgress.total_questions,
        lastPracticedAt:
          existingProgress.last_practiced_at || new Date().toISOString(),
        createdAt: existingProgress.created_at || new Date().toISOString(),
        updatedAt: existingProgress.updated_at || new Date().toISOString(),
      };
    }

    // Create new progress record
    const { data: newProgress, error: insertError } = await supabase
      .from('user_practice_progress')
      .insert({
        user_id: userId,
        catalog_id: catalogId,
        selected_state: selectedState,
        total_questions: totalQuestions,
        current_question_number: 1,
        completed_questions: '',
      })
      .select()
      .single();

    if (insertError) {
      throw insertError;
    }

    return {
      id: newProgress.id,
      userId: newProgress.user_id,
      catalogId: newProgress.catalog_id,
      currentQuestionNumber: newProgress.current_question_number,
      completedQuestions: [],
      selectedState: newProgress.selected_state,
      totalQuestions: newProgress.total_questions,
      lastPracticedAt:
        newProgress.last_practiced_at || new Date().toISOString(),
      createdAt: newProgress.created_at || new Date().toISOString(),
      updatedAt: newProgress.updated_at || new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error getting/creating practice progress:', error);
    return null;
  }
}

/**
 * Update practice progress with current question and/or completed question
 */
export async function updatePracticeProgress(
  userId: string,
  catalogId: string,
  updates: {
    currentQuestionNumber?: number;
    completedQuestionNumber?: number;
  },
  selectedState?: string | null
): Promise<PracticeProgress | null> {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Get current progress
    let query = supabase
      .from('user_practice_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('catalog_id', catalogId);

    if (selectedState) {
      query = query.eq('selected_state', selectedState);
    } else {
      query = query.is('selected_state', null);
    }

    const { data: currentProgress, error: selectError } = await query.single();

    if (selectError) {
      throw selectError;
    }

    // Parse current completed questions
    const currentCompleted = currentProgress.completed_questions
      ? currentProgress.completed_questions
          .split(',')
          .map(Number)
          .filter(Boolean)
      : [];

    // Add new completed question if provided
    const newCompleted = [...currentCompleted];
    if (
      updates.completedQuestionNumber &&
      !newCompleted.includes(updates.completedQuestionNumber)
    ) {
      newCompleted.push(updates.completedQuestionNumber);
      newCompleted.sort((a, b) => a - b); // Keep sorted
    }

    // Prepare update data
    const updateData: {
      last_practiced_at: string;
      completed_questions: string;
      current_streak?: number;
      max_streak?: number;
      current_question_number?: number;
    } = {
      last_practiced_at: new Date().toISOString(),
      completed_questions: newCompleted.join(','),
    };

    if (updates.currentQuestionNumber !== undefined) {
      updateData.current_question_number = updates.currentQuestionNumber;
    }

    // Update progress
    const { data: updatedProgress, error: updateError } = await supabase
      .from('user_practice_progress')
      .update(updateData)
      .eq('id', currentProgress.id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    return {
      id: updatedProgress.id,
      userId: updatedProgress.user_id,
      catalogId: updatedProgress.catalog_id,
      currentQuestionNumber: updatedProgress.current_question_number,
      completedQuestions: newCompleted,
      selectedState: updatedProgress.selected_state,
      totalQuestions: updatedProgress.total_questions,
      lastPracticedAt:
        updatedProgress.last_practiced_at || new Date().toISOString(),
      createdAt: updatedProgress.created_at || new Date().toISOString(),
      updatedAt: updatedProgress.updated_at || new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error updating practice progress:', error);
    return null;
  }
}

/**
 * Reset practice progress (start from beginning)
 */
export async function resetPracticeProgress(
  userId: string,
  catalogId: string,
  selectedState?: string | null
): Promise<boolean> {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    let query = supabase
      .from('user_practice_progress')
      .update({
        current_question_number: 1,
        completed_questions: '',
        last_practiced_at: new Date().toISOString(),
      })
      .eq('user_id', userId)
      .eq('catalog_id', catalogId);

    if (selectedState) {
      query = query.eq('selected_state', selectedState);
    } else {
      query = query.is('selected_state', null);
    }

    const { error } = await query;

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error resetting practice progress:', error);
    return false;
  }
}

/**
 * Get progress statistics
 */
export function getProgressStats(progress: PracticeProgress) {
  const completedCount = progress.completedQuestions.length;
  const totalQuestions = progress.totalQuestions;
  const completionPercentage =
    totalQuestions > 0
      ? Math.round((completedCount / totalQuestions) * 100)
      : 0;
  const isCompleted = completedCount >= totalQuestions && totalQuestions > 0;

  return {
    completedCount,
    totalQuestions,
    completionPercentage,
    isCompleted,
    currentQuestionNumber: progress.currentQuestionNumber,
    lastPracticedAt: new Date(progress.lastPracticedAt),
  };
}
